import React, { useEffect, useId, useMemo, useState } from 'react';
import { Image, StyleSheet, View } from 'react-native';
import Svg, { ClipPath, Defs, G, Image as SvgImage, Path, Rect, Text as SvgText } from 'react-native-svg';
import { AVATAR_MAX_CROP_OFFSET, computeServerAvatarCropRect } from '../avatar/avatarCropRect';
import { isAnimatedAvatarUri } from '../util/animatedAvatarSource';
import { AnimatedAvatarLayer } from './AnimatedAvatarLayer';
import { useVisualTheme } from '../theme/VisualThemeContext';
import { getOrDownloadAvatarUri, getOrDownloadChatAvatarUri, peekCachedAvatarUri, peekCachedChatAvatarUri } from '../util/mediaCache';
import {
  AVATAR_SHAPE_PATHS,
  DEFAULT_AVATAR_SHAPE,
  normalizeAvatarShapeId,
  type AvatarShapeId,
} from '../avatar/shapeCatalog';

type Props = {
  /** For user avatars (default). Omit when {@link chatAvatarChatId} is set. */
  userId?: string;
  /** When set, loads `/api/chats/{id}/avatar` instead of a user avatar. */
  chatAvatarChatId?: string | null;
  displayName: string;
  revision?: number | null;
  hasAvatar?: boolean | null;
  shapeId?: string | null;
  size: number;
  /** Локальный URI (file/content/blob) — предпросмотр до загрузки; рисуется в той же маске, что и серверный аватар. */
  previewUri?: string | null;
  /** Нормализованный «зум» кадра 0..1 для предпросмотра (меньше — сильнее приближение). */
  cropPreviewSide?: number;
  /** Смещение центра кропа, как на сервере (AvatarCrop), в долях короткой стороны снимка. */
  cropPreviewOffsetX?: number;
  cropPreviewOffsetY?: number;
  /** Размеры исходника в пикселях (из пикера); ускоряют первый кадр, уточняются через Image.getSize. */
  previewSourcePixelWidth?: number | null;
  previewSourcePixelHeight?: number | null;
  /** Цвет контура (viewBox 0–100). Если не задан — из темы приложения. */
  outlineColor?: string;
  /** Толщина контура в единицах viewBox (масштабируется вместе с размером). */
  outlineWidth?: number;
};

function initialsFromName(name: string): string {
  const parts = name.trim().split(/\s+/).filter(Boolean);
  if (parts.length >= 2) {
    const a = parts[0][0];
    const b = parts[1][0];
    if (a && b) return `${a}${b}`.toUpperCase();
  }
  const s = name.trim();
  if (s.length >= 2) return s.slice(0, 2).toUpperCase();
  return s.slice(0, 1).toUpperCase() || '?';
}

const DEFAULT_OUTLINE_WIDTH = 2.25;

export function AvatarBadge({
  userId,
  chatAvatarChatId,
  displayName,
  revision,
  hasAvatar,
  shapeId,
  size,
  previewUri,
  cropPreviewSide = 1,
  cropPreviewOffsetX = 0,
  cropPreviewOffsetY = 0,
  previewSourcePixelWidth = null,
  previewSourcePixelHeight = null,
  outlineColor,
  outlineWidth = DEFAULT_OUTLINE_WIDTH,
}: Props): React.ReactElement {
  const { avatarChrome } = useVisualTheme();
  const outlineStroke = outlineColor ?? avatarChrome.outline;
  const shape: AvatarShapeId = normalizeAvatarShapeId(shapeId ?? DEFAULT_AVATAR_SHAPE);
  const path = AVATAR_SHAPE_PATHS[shape];
  const rev = typeof revision === 'number' && Number.isFinite(revision) ? revision : 0;
  const chatId = typeof chatAvatarChatId === 'string' && chatAvatarChatId.length > 0 ? chatAvatarChatId : null;
  const uid = typeof userId === 'string' && userId.length > 0 ? userId : null;
  const wantImage = Boolean(hasAvatar !== false && rev > 0 && (chatId != null || uid != null));

  /**
   * Какой серверный аватар нужен сейчас; `null` — никакой (предпросмотр, аватара нет).
   * Показанный URI помнит, к какому ключу он относится: при смене ключа прежняя картинка
   * остаётся на месте, пока новая не пришла, — кроме случая, когда картинка не нужна вовсе.
   */
  const sourceKey = previewUri || !wantImage ? null : chatId ? `chat:${chatId}:${rev}` : uid ? `user:${uid}:${rev}` : null;
  const peekHot = (): string | null => {
    if (chatId) return peekCachedChatAvatarUri(chatId, rev);
    if (uid) return peekCachedAvatarUri(uid, rev);
    return null;
  };
  const [shownUri, setShownUri] = useState<{ key: string | null; uri: string | null }>(() => ({
    key: sourceKey,
    uri: sourceKey == null ? null : peekHot(),
  }));
  let uri = shownUri.uri;
  if (shownUri.key !== sourceKey) {
    uri = sourceKey == null ? null : (peekHot() ?? shownUri.uri);
    setShownUri({ key: sourceKey, uri });
  }

  const clipId = `avc${useId()}`;

  useEffect(() => {
    if (sourceKey == null) return;
    const hot = chatId ? peekCachedChatAvatarUri(chatId, rev) : uid ? peekCachedAvatarUri(uid, rev) : null;
    // Горячий URI уже показан тем же рендером, в котором сменился ключ.
    if (hot) return;
    let cancelled = false;
    const download = chatId ? getOrDownloadChatAvatarUri(chatId, rev) : uid ? getOrDownloadAvatarUri(uid, rev) : null;
    void download
      ?.then((u) => {
        if (!cancelled) setShownUri({ key: sourceKey, uri: u });
      })
      // Не скачалось — остаются инициалы. 403 здесь штатный: аватар виден только тем, с кем
      // есть общий чат, а бейдж рисуют и в поиске, и в админке. Без catch отказ уходил в
      // unhandledrejection и будил бота как ошибка (GlitchTip #21).
      .catch(() => {});
    return () => {
      cancelled = true;
    };
  }, [sourceKey, chatId, uid, rev]);

  const isLocalPreview = Boolean(previewUri && previewUri.length > 0);
  const seedW =
    typeof previewSourcePixelWidth === 'number' && previewSourcePixelWidth > 0
      ? Math.round(previewSourcePixelWidth)
      : null;
  const seedH =
    typeof previewSourcePixelHeight === 'number' && previewSourcePixelHeight > 0
      ? Math.round(previewSourcePixelHeight)
      : null;
  /** Размеры из пикера — первый кадр до ответа `Image.getSize`; ключ — снимок и эти размеры. */
  const previewKey = isLocalPreview && previewUri ? `${previewUri}|${seedW ?? ''}|${seedH ?? ''}` : null;
  const seed = previewKey != null && seedW != null && seedH != null ? { w: seedW, h: seedH } : null;
  const [intrinsicState, setIntrinsicState] = useState<{
    key: string | null;
    size: { w: number; h: number } | null;
  }>(() => ({ key: previewKey, size: seed }));
  let previewIntrinsic = intrinsicState.size;
  if (intrinsicState.key !== previewKey) {
    previewIntrinsic = seed;
    setIntrinsicState({ key: previewKey, size: seed });
  }

  useEffect(() => {
    if (previewKey == null || !previewUri) return;
    let cancelled = false;
    Image.getSize(
      previewUri,
      (w, h) => {
        if (cancelled || !Number.isFinite(w) || !Number.isFinite(h) || w < 1 || h < 1) {
          return;
        }
        setIntrinsicState({ key: previewKey, size: { w: Math.round(w), h: Math.round(h) } });
      },
      () => {
        if (cancelled) {
          return;
        }
        if (seedW != null && seedH != null) {
          setIntrinsicState({ key: previewKey, size: { w: seedW, h: seedH } });
        }
      },
    );
    return () => {
      cancelled = true;
    };
  }, [previewKey, previewUri, seedW, seedH]);

  const initials = useMemo(() => initialsFromName(displayName), [displayName]);

  const remoteHref = wantImage && uri ? uri : null;
  const imageHref = previewUri && previewUri.length > 0 ? previewUri : remoteHref;
  // Граница та же, что у сервера (`AvatarCrop.validate`) и у редактора: смещение меряется в
  // коротких сторонах снимка, и на широком кадре оно честно больше половины — прежние ±0.51
  // обрезали превью там, где сам кадр законный.
  const ox =
    previewUri && Number.isFinite(cropPreviewOffsetX)
      ? Math.max(-AVATAR_MAX_CROP_OFFSET, Math.min(AVATAR_MAX_CROP_OFFSET, cropPreviewOffsetX))
      : 0;
  const oy =
    previewUri && Number.isFinite(cropPreviewOffsetY)
      ? Math.max(-AVATAR_MAX_CROP_OFFSET, Math.min(AVATAR_MAX_CROP_OFFSET, cropPreviewOffsetY))
      : 0;
  const sideNorm =
    previewUri && cropPreviewSide > 0 && cropPreviewSide <= 1 ? cropPreviewSide : 1;

  const serverCropLayout = useMemo(() => {
    if (!isLocalPreview || !previewIntrinsic) {
      return null;
    }
    const { w, h } = previewIntrinsic;
    const { x0, y0, sidePx } = computeServerAvatarCropRect(w, h, sideNorm, ox, oy);
    if (sidePx < 2) {
      return null;
    }
    const s = 100 / sidePx;
    return { x: -x0 * s, y: -y0 * s, imgW: w * s, imgH: h * s };
  }, [isLocalPreview, ox, oy, previewIntrinsic, sideNorm]);

  const fontSize = Math.max(18, Math.min(40, Math.round(34 * (size / 88))));

  /**
   * Живая аватарка (GIF) идёт отдельным слоем: `react-native-svg` рисует только первый кадр.
   * Кадрирование предпросмотра при этом остаётся за SVG — пока снимок не отправлен, крутить
   * анимацию не нужно, а рамку кадра пользователь двигает именно там.
   *
   * <p>Слой нужен на всех трёх платформах. Про веб раньше считалось, что браузер анимирует
   * `<image>` внутри SVG сам; в Chrome так и есть, а WebKit показывает первый кадр — на iPhone
   * аватарка оживала, только если открыть снимок во весь экран (жалоба владельца 2026-09-05).
   * Поэтому в вебе слой рисует обычный `<img>` (см. `AnimatedAvatarLayer.web`).
   */
  const animatedHref = !isLocalPreview && isAnimatedAvatarUri(imageHref) ? imageHref : null;

  return (
    <View style={{ width: size, height: size }}>
      {animatedHref ? (
        <View style={StyleSheet.absoluteFill} pointerEvents="none">
          <AnimatedAvatarLayer uri={animatedHref} size={size} pathD={path} />
        </View>
      ) : null}
      <Svg width={size} height={size} viewBox="0 0 100 100">
        <Defs>
          <ClipPath id={clipId}>
            <Path d={path} />
          </ClipPath>
        </Defs>
        <G clipPath={`url(#${clipId})`}>
          {animatedHref ? null : imageHref ? (
            <>
              {isLocalPreview && !serverCropLayout ? (
                <Rect x={0} y={0} width={100} height={100} fill={avatarChrome.previewSlotBg} />
              ) : null}
              {isLocalPreview && serverCropLayout ? (
                <SvgImage
                  x={serverCropLayout.x}
                  y={serverCropLayout.y}
                  width={serverCropLayout.imgW}
                  height={serverCropLayout.imgH}
                  preserveAspectRatio="none"
                  href={imageHref}
                />
              ) : (
                <SvgImage
                  width={100}
                  height={100}
                  preserveAspectRatio="xMidYMid slice"
                  href={imageHref}
                />
              )}
            </>
          ) : (
            <>
              <Rect x={0} y={0} width={100} height={100} fill="rgba(59,130,246,0.35)" />
              <SvgText
                x={50}
                y={58}
                textAnchor="middle"
                fill="#0f172a"
                fontSize={fontSize}
                fontWeight="700">
                {initials}
              </SvgText>
            </>
          )}
        </G>
        <Path
          d={path}
          fill="none"
          stroke={outlineStroke}
          strokeWidth={outlineWidth}
          strokeLinejoin="round"
          strokeLinecap="round"
        />
      </Svg>
    </View>
  );
}
